/**
 * API クライアント（SPEC §3 / §5）。
 *
 * 「再送すれば成功しうる失敗（NetworkError / 5xx）」と
 * 「再送しても成功しない失敗（4xx）」を呼び出し側が区別できるように
 * 例外型を分けている。リトライキューの分岐がこれに依存する。
 */
import { getToken } from './token';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** 接続できなかった（オフライン等）。リクエストがサーバに届いていない。 */
export class NetworkError extends Error {
  constructor(message = 'ネットワークに接続できません') {
    super(message);
    this.name = 'NetworkError';
  }
}

export interface ApiRequest {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
}

export async function apiFetch<T>(path: string, req: ApiRequest = {}): Promise<T> {
  const token = getToken();
  if (!token) throw new ApiError(401, 'unauthorized', 'API トークンが未設定です');

  const headers: Record<string, string> = { authorization: `Bearer ${token}` };
  if (req.body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(path, {
      method: req.method ?? 'GET',
      headers,
      body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
      cache: 'no-store',
    });
  } catch {
    throw new NetworkError();
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // API は常に JSON を返す想定。HTML が返ってきたらルーティング事故。
      data = null;
    }
  }

  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'http_error', err?.message ?? `HTTP ${res.status}`);
  }

  return data as T;
}
